import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, Search } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { testIds } from "@/constants/testIds/b2b";

export default function TopBar() {
  const { user, isApproved, isAdmin, logout } = useAuth();
  const { totalUnits, openDrawer } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    nav("/");
  };

  return (
    <header className="sticky top-0 z-30 h-[var(--topbar-h)] bg-white border-b border-border overflow-visible" data-testid="site-navbar">
      <div className="h-full px-4 md:px-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 md:gap-6 min-w-0">
          <button onClick={() => setMenuOpen((v) => !v)} className="md:hidden p-2 -ml-2 hover:bg-secondary rounded" aria-label="Menu" data-testid="navbar-mobile-toggle">
            <Menu size={18} strokeWidth={1.5} />
          </button>
          <Link to={user ? "/dashboard" : "/"} className="shrink-0 flex items-center -my-2" data-testid="navbar-logo" aria-label="Rowen Kids Club">
            <img
              src="/brand/rowen-logo.png"
              alt="Rowen Kids Club"
              className="h-24 md:h-28 w-auto object-contain select-none transition-opacity hover:opacity-80"
              draggable="false"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <TopNavLink to="/shop" testid="nav-link-shop">Products</TopNavLink>
            {isApproved && <TopNavLink to="/orders" testid={testIds.navbarMyOrders}>Orders</TopNavLink>}
            {user && !isAdmin && <TopNavLink to="/account" testid="nav-link-account">Account</TopNavLink>}
            {isAdmin && <TopNavLink to="/admin" testid={testIds.navbarAdmin}>Admin</TopNavLink>}
          </nav>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          {user ? (
            <>
              {!isApproved && !isAdmin && (
                <span data-testid={testIds.navbarApprovalBadge} className="hidden md:inline-flex text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">
                  Pending approval
                </span>
              )}
              <div className="hidden md:flex items-center gap-3 pr-2 pl-3 border-l border-border h-8">
                <span className="text-xs text-muted-foreground truncate max-w-[160px]">{user.business_name || user.email}</span>
                <button onClick={handleLogout} data-testid={testIds.navbarLogout} className="text-xs uppercase tracking-[0.12em] text-muted-foreground hover:text-foreground">
                  Sign out
                </button>
              </div>
            </>
          ) : (
            <div className="hidden md:flex items-center gap-3">
              <NavLink to="/login" data-testid={testIds.navbarLogin} className="text-sm text-muted-foreground hover:text-foreground">
                Sign in
              </NavLink>
              <NavLink to="/register" data-testid={testIds.navbarRegister} className="text-sm px-3.5 py-1.5 rounded bg-primary text-primary-foreground hover:opacity-90 transition-opacity">
                Apply
              </NavLink>
            </div>
          )}

          <button
            onClick={openDrawer}
            data-testid={testIds.navbarCart}
            className="relative p-2 hover:bg-secondary rounded transition-colors"
            aria-label="Open order pad"
          >
            <ShoppingBag size={18} strokeWidth={1.5} />
            {totalUnits > 0 && (
              <span className="absolute -top-0.5 -right-0.5 text-[10px] bg-primary text-primary-foreground rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center tabular-nums">
                {totalUnits}
              </span>
            )}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-border bg-white" data-testid="navbar-mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            <MobileLink to="/shop" testid="mobile-nav-shop" onClick={() => setMenuOpen(false)}>Products</MobileLink>
            {isApproved && <MobileLink to="/orders" testid="mobile-nav-orders" onClick={() => setMenuOpen(false)}>Orders</MobileLink>}
            {user && !isAdmin && <MobileLink to="/account" testid="mobile-nav-account" onClick={() => setMenuOpen(false)}>Account</MobileLink>}
            {isAdmin && <MobileLink to="/admin" testid="mobile-nav-admin" onClick={() => setMenuOpen(false)}>Admin</MobileLink>}
            {user ? (
              <button onClick={handleLogout} className="text-left px-3 py-2 text-sm" data-testid="mobile-nav-logout">Sign out</button>
            ) : (
              <>
                <MobileLink to="/login" testid="mobile-nav-login" onClick={() => setMenuOpen(false)}>Sign in</MobileLink>
                <MobileLink to="/register" testid="mobile-nav-register" onClick={() => setMenuOpen(false)}>Apply for wholesale</MobileLink>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function TopNavLink({ to, children, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `px-3 py-1.5 rounded text-sm transition-colors ${
          isActive ? "text-foreground bg-secondary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function MobileLink({ to, children, testid, onClick }) {
  return (
    <NavLink to={to} onClick={onClick} data-testid={testid} className="px-3 py-2 text-sm hover:bg-secondary rounded">
      {children}
    </NavLink>
  );
}
